import { type BhkConfig, type FilterOptions, type ListingStatus } from '@resolute/schema';
import {
  BedDouble,
  Building2,
  CalendarClock,
  Hammer,
  IndianRupee,
  LayoutGrid,
  MapPin,
  Ruler,
  type LucideIcon,
} from 'lucide-react';

import { buildSearchUrl } from '@/features/search/search-url';

/**
 * Data-driven navigation / mega-menu configuration.
 *
 * Every mega-menu item is a *predefined search query*: its `to` is produced by
 * {@link buildSearchUrl}, which serialises a partial search state into the same
 * URL params the results page parses. So clicking a menu item is identical to
 * typing that query into the Hero search bar — one search system, shared state,
 * shared URL, shared API. The SEO-friendly landing path (`/inventory`) renders
 * the same `SearchPage`, which restores the filters from the URL and executes
 * the search automatically.
 *
 * The Inventory mega-menu's taxonomy is NOT declared here — it is built at
 * render time from `GET /filter-options` by {@link buildNavMenus}, the same
 * source the search filter panel uses. So categories, construction stages,
 * property types and cities always match what the upstream inventory actually
 * contains; nothing has to be kept in sync by hand. Budget and carpet area are
 * the exceptions (see {@link STATIC_GROUPS}). `icon`/`description`/
 * `eyebrow`/`cta`/`sublabel` are presentation-only metadata for the two-panel
 * mega menu; they never affect the links, URL generation or search behaviour.
 */

export interface MenuLink {
  label: string;
  to: string;
  /** Presentation-only: muted subtitle shown under the label in the mega menu. */
  sublabel?: string;
}
export interface MenuGroup {
  heading: string;
  items: MenuLink[];
  /** Presentation-only: sidebar icon + right-panel description. */
  icon?: LucideIcon;
  description?: string;
}
export interface NavMenu {
  id: string;
  label: string;
  groups: MenuGroup[];
  /** Small uppercase label above the sidebar categories. */
  eyebrow?: string;
  /** Optional CTA pinned to the bottom of the dark sidebar. */
  cta?: MenuLink;
}

const INVENTORY_PATH = '/inventory';

/**
 * The two hand-declared groups in the Inventory menu, and deliberately so:
 * CPzone exposes no price-band or area-band vocabulary (`/filter-options`
 * returns `priceBands: []`), so there is nothing upstream to source. Both are
 * bucketings of a continuous numeric filter, not a taxonomy that can drift from
 * the inventory. Breakpoints match the hero search's budget and area presets —
 * see `hero-search/category-config.ts`.
 *
 * Bands are contiguous and open-ended at both ends, so every value is covered.
 * Each band's upper bound is the next band's lower bound; the search API treats
 * the range as inclusive, so a property sitting exactly on a boundary appears
 * in both neighbouring bands — intended for a browse menu.
 */
const BUDGET_GROUP: MenuGroup = {
  heading: 'Budget',
  icon: IndianRupee,
  description: 'Browse inventory within your budget.',
  items: [
    { label: 'Under ₹50 L', to: buildSearchUrl(INVENTORY_PATH, { priceMax: 5_000_000 }) },
    {
      label: '₹50 L – ₹1 Cr',
      to: buildSearchUrl(INVENTORY_PATH, { priceMin: 5_000_000, priceMax: 10_000_000 }),
    },
    {
      label: '₹1 – 2 Cr',
      to: buildSearchUrl(INVENTORY_PATH, { priceMin: 10_000_000, priceMax: 20_000_000 }),
    },
    {
      label: '₹2 – 3 Cr',
      to: buildSearchUrl(INVENTORY_PATH, { priceMin: 20_000_000, priceMax: 30_000_000 }),
    },
    {
      label: '₹3 – 5 Cr',
      to: buildSearchUrl(INVENTORY_PATH, { priceMin: 30_000_000, priceMax: 50_000_000 }),
    },
    { label: '₹5 Cr+', to: buildSearchUrl(INVENTORY_PATH, { priceMin: 50_000_000 }) },
  ],
};

/**
 * `areaMin`/`areaMax` are in sq.ft and reach CPzone as `min_rera_area` /
 * `max_rera_area` — the RERA carpet area, which is what the results filter
 * panel calls this too. Headed "RERA Carpet Area" rather than plain "Area" on
 * purpose: `SearchState.areas` is a *different* filter (locality), and the
 * filter panel already spends the bare word "Area" on it.
 */
const AREA_GROUP: MenuGroup = {
  heading: 'RERA Carpet Area',
  icon: Ruler,
  description: 'Browse inventory by RERA carpet area.',
  items: [
    { label: 'Under 500 sq.ft', to: buildSearchUrl(INVENTORY_PATH, { areaMax: 500 }) },
    {
      label: '500 – 1,000 sq.ft',
      to: buildSearchUrl(INVENTORY_PATH, { areaMin: 500, areaMax: 1_000 }),
    },
    {
      label: '1,000 – 1,500 sq.ft',
      to: buildSearchUrl(INVENTORY_PATH, { areaMin: 1_000, areaMax: 1_500 }),
    },
    {
      label: '1,500 – 2,000 sq.ft',
      to: buildSearchUrl(INVENTORY_PATH, { areaMin: 1_500, areaMax: 2_000 }),
    },
    {
      label: '2,000 – 3,000 sq.ft',
      to: buildSearchUrl(INVENTORY_PATH, { areaMin: 2_000, areaMax: 3_000 }),
    },
    { label: '3,000+ sq.ft', to: buildSearchUrl(INVENTORY_PATH, { areaMin: 3_000 }) },
  ],
};

/** Hand-declared groups — see BUDGET_GROUP/AREA_GROUP. */
const STATIC_GROUPS: MenuGroup[] = [BUDGET_GROUP, AREA_GROUP];

/**
 * Builds the top-level menus from live filter options.
 *
 * Every group is API-backed, so passing `undefined` (while `/filter-options`
 * is in flight, or when it failed) yields a menu with NO groups rather than
 * empty shells or invented placeholder entries. The header renders that case
 * as a plain "Inventory" link instead of an empty mega-menu — see
 * `NavDropdown` in `header.tsx`.
 */
export function buildNavMenus(options?: FilterOptions): NavMenu[] {
  const groups: MenuGroup[] = [
    {
      heading: 'Category',
      icon: LayoutGrid,
      description: 'Browse inventory by property category.',
      items: (options?.categories ?? []).map((c) => ({
        label: c.label,
        to: buildSearchUrl(INVENTORY_PATH, { category: c.value }),
      })),
    },
    {
      heading: 'Stage',
      icon: Hammer,
      description: 'Discover inventory at every construction stage.',
      // `listingStatuses` is derived server-side from LISTING_STATUSES, the
      // same enum `SearchState.listingStatus` is typed against — so every
      // value here is a valid member; the cast just re-states that.
      items: (options?.listingStatuses ?? []).map((s) => ({
        label: s.label,
        to: buildSearchUrl(INVENTORY_PATH, { listingStatus: [s.value as ListingStatus] }),
      })),
    },
    {
      heading: 'Type',
      icon: Building2,
      description: 'Every property type in the inventory.',
      items: (options?.propertyTypes ?? []).map((t) => ({
        label: t.label,
        to: buildSearchUrl(INVENTORY_PATH, { type: [t.value] }),
      })),
    },
    {
      heading: 'BHK',
      icon: BedDouble,
      description: 'Filter by unit configuration.',
      // `bhkConfigs` is derived server-side from BHK_CONFIGS, the same enum
      // `SearchState.config` is typed against — so every value is a valid
      // member; the cast just re-states that. Note this is the full supported
      // vocabulary, not only configurations with inventory right now (same as
      // every other group, and as the search filter panel).
      items: (options?.bhkConfigs ?? []).map((b) => ({
        label: b.label,
        to: buildSearchUrl(INVENTORY_PATH, { config: [b.value as BhkConfig] }),
      })),
    },
    BUDGET_GROUP,
    AREA_GROUP,
    {
      heading: 'Possession Year',
      icon: CalendarClock,
      description: 'Find inventory completing when you need it.',
      // The search API takes a possession date RANGE, so each year becomes its
      // own full-year window. Years come from the live inventory, so every
      // entry here has something completing in it.
      items: (options?.possessionYears ?? []).map((y) => ({
        label: y.label,
        to: buildSearchUrl(INVENTORY_PATH, {
          possessionStart: `${y.value}-01-01`,
          possessionEnd: `${y.value}-12-31`,
        }),
      })),
    },
    {
      heading: 'City',
      icon: MapPin,
      description: 'Inventory in your preferred city.',
      // `value` is the upstream city id, which the search API forwards
      // straight through as `city_ids` — a city *name* here matches nothing.
      items: (options?.cities ?? []).map((c) => ({
        label: c.label,
        to: buildSearchUrl(INVENTORY_PATH, { city: c.value }),
      })),
    },
  ];

  const populated = groups.filter((g) => g.items.length > 0);
  // The static groups always survive that filter — which would otherwise defeat
  // the no-groups fallback documented above and leave a mega-menu holding
  // nothing but price/area bands while `/filter-options` is in flight (or after
  // it failed). Keep the plain-link fallback for that case.
  const hasApiGroup = populated.some((g) => !STATIC_GROUPS.includes(g));

  return [
    {
      id: 'inventory',
      label: 'Inventory',
      eyebrow: 'New developments',
      cta: { label: 'View inventory', to: INVENTORY_PATH },
      groups: hasApiGroup ? populated : [],
    },
  ];
}

export interface SimpleNavLink {
  type: 'link';
  label: string;
  to: string;
}
export interface SimpleNavGroup {
  heading?: string;
  items: MenuLink[];
}
export interface SimpleNavDropdown {
  type: 'dropdown';
  id: string;
  label: string;
  items?: MenuLink[];
  groups?: SimpleNavGroup[];
}
export type SimpleNavItem = SimpleNavLink | SimpleNavDropdown;

/**
 * Top-level nav items after the Inventory mega-menu, in
 * display order. "Services" sits directly after "Inventory".
 * Rendered by `NavSimpleDropdown` in `header.tsx`.
 */
export const SIMPLE_NAV_ITEMS: SimpleNavItem[] = [
  {
    type: 'dropdown',
    id: 'services',
    label: 'Services',
    groups: [
      {
        heading: 'Categories',
        items: [
          { label: 'Residential', to: buildSearchUrl(INVENTORY_PATH, { category: 'residential' }) },
          { label: 'Commercial', to: buildSearchUrl(INVENTORY_PATH, { category: 'commercial' }) },
          { label: 'Industrial', to: buildSearchUrl(INVENTORY_PATH, { category: 'industrial' }) },
          { label: 'Land & Plots', to: buildSearchUrl(INVENTORY_PATH, { category: 'land' }) },
        ],
      },
      {
        heading: 'Specialized Services',
        items: [
          { label: 'Group Booking', to: '/services/group-booking' },
          { label: 'BTS (Built-to-Suit)', to: '/services/built-to-suit' },
          { label: 'Investment Advisory', to: '/services/investment-advisory' },
          { label: 'Mandate', to: '/services/mandate' },
        ],
      },
    ],
  },
  { type: 'link', label: 'Calculators', to: '/calculators' },
  { type: 'link', label: 'Vaastu Tips', to: '/vaastu-tips' },
  {
    type: 'dropdown',
    id: 'about',
    label: 'About',
    items: [
      { label: 'About Us', to: '/about' },
      { label: 'Leadership', to: '/about/leadership' },
    ],
  },
  { type: 'link', label: 'Contact Us', to: '/contact' },
  { type: 'link', label: 'Careers', to: '/careers' },
];

